/**
 * The buyer's car follows them across pages (P25009-482 BR-4 / AC-7).
 *
 * A registration number is typed once. After that every page — a listing
 * opened in a new tab, a reload, the search results — knows the car without
 * asking again, and "Byt bil" offers the recent cars as one-tap chips.
 *
 * Since P25009-491 a car can also be chosen WITHOUT a plate, by make, model
 * and version from the car database. Such a car has no reg; it is known by its
 * car-database id (`carId`, the fitment row's wpId), which the search URL
 * carries the way it carries `regNumber` for a plate.
 *
 * localStorage keys:
 *   wheelplace.regNumber   the current plate (existing key, many readers);
 *                          absent while the car was chosen by model
 *   wheelplace.car         { reg, carId, series, data } — the car's payload, so
 *                          a new tab rebuilds the car without asking again
 *   wheelplace.recentCars  up to ten { reg|null, carId, label, ts, series,
 *                          data? }, newest first — plates and models alike
 *                          (BR-7). Replaces wheelplace.recentRegs (three
 *                          plates), which is read once and moved over.
 *
 * `wheelplace.car` only counts while it belongs to the current plate — or, for
 * a car chosen by model, while there is no current plate. Every existing
 * "forget the car" path (Ny sökning on the search page, logout) removes the
 * regNumber, and that alone is enough to stop a plate car coming back.
 *
 * Nothing here runs on the server: every storage read is guarded, and the
 * rehydration lives in a useEffect.
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLicensePlateInfo, searchCarModel, updateUserProfileGeneral } from './api';
import {
  CLEAR_CURRENT_LICENSE_PLATE,
  SET_LICENSE_PLATE_INFO,
} from '../ducks/currentLicensePlate.duck';
import { CLEAR_CAR } from '../ducks/currentCar.duck';
import { plateInfoCar, plateInfoCarName } from './wheelFitment';
import { modelCarName } from './carVersion';

export const REG_STORAGE_KEY = 'wheelplace.regNumber';
export const SERIES_STORAGE_KEY = 'wheelplace.regSeries';
export const CAR_STORAGE_KEY = 'wheelplace.car';
export const RECENT_CARS_KEY = 'wheelplace.recentCars';
/* The three-plate list before P25009-491 — moved into RECENT_CARS_KEY once. */
export const RECENT_REGS_KEY = 'wheelplace.recentRegs';
export const MAX_RECENT_CARS = 10;
/* Set by forgetCar, cleared by the next rememberCar: "the buyer took the car
 * away on purpose". Without it a signed-in buyer's profile plate brings the
 * car straight back on the next render (useCurrentCar falls back to the
 * profile when this browser has no car). */
const CAR_CLEARED_KEY = 'wheelplace.carCleared';

/* A plate payload is a few kilobytes; a pathological one is not worth the
 * storage quota. Above this the car is remembered by plate only and looked up
 * again when it is picked. */
const MAX_STORED_PAYLOAD_CHARS = 60000;

const CAR_EVENT = 'wp:carchange';

/* Bumped when stored car payloads can no longer be trusted. 2 (P25009-492):
 * the plate lookup used to resolve many XC60s (and other cars with a special
 * version) to that special version — a cached payload from before is dropped
 * and looked up again, once, so the corrected car replaces it. 3
 * (P25009-491): the lookup now carries the registry's model year, so a car
 * cached before is named by its generation's first year until looked up again
 * (BR-8 — "2017" on the search page, "2021" in the saved list). */
export const CAR_STORAGE_VERSION = 3;

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const readString = key => {
  try {
    return storage()?.getItem(key) || null;
  } catch {
    return null;
  }
};

const readJson = key => {
  const raw = readString(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    const s = storage();
    if (!s) return;
    if (value === null || value === undefined) s.removeItem(key);
    else s.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch {
    /* quota / private mode — the car just is not remembered */
  }
};

/** "ABC 123" / "abc-123" -> "ABC123". */
export const normalizeReg = reg =>
  String(reg || '')
    .toUpperCase()
    .replace(/[\s-]/g, '');

/** "ABC123" -> "ABC 123", the way the plate input shows it. */
export const formatReg = reg => {
  const clean = normalizeReg(reg);
  return clean.length > 3 ? `${clean.slice(0, 3)} ${clean.slice(3)}` : clean;
};

/* The plate lookup's half-way answers: the buyer still has to pick a model
 * or an engine series in RegNumInput's window. */
const PICK_STEPS = ['choose_series', 'choose_Modal'];

/** A payload that actually describes a car (not a "choose model / series" step). */
export const isCarPayload = data => !!plateInfoCar(data) && !PICK_STEPS.includes(data?.status);

const storablePayload = data => {
  try {
    const json = JSON.stringify(data);
    return json.length <= MAX_STORED_PAYLOAD_CHARS ? data : null;
  } catch {
    return null;
  }
};

/**
 * The car-database id of a car payload (the fitment row's wpId), as a string,
 * or null. A plate resolved to the car database has one too; a raw registry
 * answer does not.
 */
export const carIdOf = data => {
  const car = plateInfoCar(data);
  const id = car?.id ?? car?.source?.wpId;
  return id != null && /^\d+$/.test(String(id)) ? String(id) : null;
};

/**
 * The car's name as the search page shows it: "Volvo XC60 2021" for a plate
 * (with the registry's model year, BR-8), "Volkswagen ID.4 (E2) 2020–" for a
 * car chosen by model — the version is what tells two XC60s apart.
 */
export const carLabel = (data, reg) =>
  (normalizeReg(reg) ? plateInfoCarName(data) : modelCarName(data)) || plateInfoCarName(data);

/**
 * Is this search the car's own search? The URL names the car: `regNumber`
 * for a plate car, `carId` for a car chosen by model (P25009-491). A shared
 * link or an older search can name another car, and must not be taken for
 * this one.
 *
 * @param {string|URLSearchParams} search location.search
 * @param {Object} car { data, reg } — the car in Redux
 */
export const isCarsOwnSearch = (search, { data, reg } = {}) => {
  if (!isCarPayload(data)) return false;
  const query = search instanceof URLSearchParams ? search : new URLSearchParams(search || '');
  const plate = normalizeReg(reg);
  if (plate) return normalizeReg(query.get('regNumber')) === plate;
  const carId = carIdOf(data);
  return !!carId && !query.get('regNumber') && query.get('carId') === carId;
};

/** Which recent entry this is: one per plate, one per car chosen by model. */
export const recentCarKey = entry => {
  const reg = normalizeReg(entry?.reg);
  if (reg) return `reg:${reg}`;
  return entry?.carId ? `car:${entry.carId}` : null;
};

/* The old three-plate list ({ reg, series, name, data?, v? }) moves into the
 * new store the first time the new one is read. */
const migrateRecentRegs = () => {
  const old = readJson(RECENT_REGS_KEY);
  if (!Array.isArray(old)) return [];
  const now = Date.now();
  const moved = old
    .filter(entry => entry && normalizeReg(entry.reg))
    .map((entry, i) => ({
      reg: normalizeReg(entry.reg),
      carId: carIdOf(entry.data),
      label: entry.name || '',
      ts: now - i,
      series: entry.series || null,
      ...(entry.data ? { data: entry.data, v: entry.v } : {}),
    }));
  write(RECENT_CARS_KEY, moved);
  write(RECENT_REGS_KEY, null);
  return moved;
};

/**
 * The last cars this browser searched for, newest first: plates and cars
 * chosen by model, each { reg|null, carId, label, ts, series, data? }.
 */
export const getRecentCars = () => {
  const stored = readJson(RECENT_CARS_KEY);
  const list = Array.isArray(stored) ? stored : migrateRecentRegs();
  /* An older entry keeps its plate and label but not its payload, so picking
   * it looks the car up again (CAR_STORAGE_VERSION). */
  return list
    .filter(entry => entry && recentCarKey(entry))
    .map(entry => {
      if (entry.v === CAR_STORAGE_VERSION || !entry.data) return entry;
      const { data, ...rest } = entry;
      return rest;
    });
};

const notifyCarChange = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CAR_EVENT));
  }
};

/** Take one entry off the recent list ("✕" on its chip). */
export const removeRecentCar = entry => {
  const key = recentCarKey(entry);
  write(
    RECENT_CARS_KEY,
    getRecentCars().filter(r => recentCarKey(r) !== key)
  );
  notifyCarChange();
};

/** "Rensa historik" — the recent list only; the car on screen stays. */
export const clearRecentCars = () => {
  write(RECENT_CARS_KEY, []);
  notifyCarChange();
};

/**
 * The current car's cached payload: the plate car when it belongs to the
 * current plate, or the car chosen by model while there is no plate.
 */
export const readStoredCar = () => {
  const reg = normalizeReg(readString(REG_STORAGE_KEY));
  const car = readJson(CAR_STORAGE_KEY);
  if (!car || car.v !== CAR_STORAGE_VERSION || !isCarPayload(car.data)) return null;
  if (normalizeReg(car.reg)) return reg && normalizeReg(car.reg) === reg ? car : null;
  return !reg && car.carId ? car : null;
};

/** The current plate, as stored. */
export const readStoredReg = () => normalizeReg(readString(REG_STORAGE_KEY)) || null;

/**
 * Remember a car that was just chosen: the current car, its payload, and the
 * front of the recent list. Called from every successful plate lookup, and
 * with `reg: null` for a car chosen by make, model and version.
 */
export const rememberCar = ({ reg, series = null, data }) => {
  const clean = normalizeReg(reg);
  const carId = carIdOf(data);
  if (!isCarPayload(data) || (!clean && !carId)) return;
  const payload = storablePayload(data);

  write(REG_STORAGE_KEY, clean || null);
  write(SERIES_STORAGE_KEY, clean ? series || null : null);
  write(
    CAR_STORAGE_KEY,
    payload
      ? {
          reg: clean || null,
          carId,
          series: clean ? series || null : null,
          data: payload,
          v: CAR_STORAGE_VERSION,
        }
      : null
  );
  /* A car chosen by model has no plate to store, and a signed-in buyer's
   * profile plate must not come back over it (useCurrentCar falls back to the
   * profile when this browser has no plate). */
  write(CAR_CLEARED_KEY, clean ? null : '1');

  const entry = {
    reg: clean || null,
    carId,
    label: carLabel(data, clean),
    ts: Date.now(),
    series: clean ? series || null : null,
    ...(payload ? { data: payload, v: CAR_STORAGE_VERSION } : {}),
  };
  const key = recentCarKey(entry);
  const recents = [entry, ...getRecentCars().filter(r => recentCarKey(r) !== key)].slice(
    0,
    MAX_RECENT_CARS
  );
  write(RECENT_CARS_KEY, recents);

  notifyCarChange();
};

/**
 * Take the current car away — "Byt bil" on the search page. Clears it from
 * Redux, from this browser, and from a signed-in buyer's profile, so no page
 * brings it back. The recent-cars list is kept: those plates are what the
 * buyer picks from next.
 */
export const forgetCar = ({ dispatch, userId } = {}) => {
  write(REG_STORAGE_KEY, null);
  write(SERIES_STORAGE_KEY, null);
  write(CAR_STORAGE_KEY, null);
  write(CAR_CLEARED_KEY, '1');
  if (dispatch) {
    dispatch({ type: CLEAR_CURRENT_LICENSE_PLATE });
    dispatch({ type: CLEAR_CAR });
  }
  if (userId) {
    updateUserProfileGeneral({
      params: { id: userId, publicData: { regNumber: null, regSeries: null } },
    }).catch(() => {});
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CAR_EVENT));
  }
};

/* One lookup per plate at a time, however many components ask for it. */
const inFlight = new Map();
const lookupPlate = ({ reg, series }) => {
  const key = `${reg}|${series || ''}`;
  if (!inFlight.has(key)) {
    const promise = getLicensePlateInfo(
      series ? { licensePlate: reg, selectedSeries: series } : { licensePlate: reg }
    ).finally(() => inFlight.delete(key));
    inFlight.set(key, promise);
  }
  return inFlight.get(key);
};

/* One fetch per car-database id at a time, like lookupPlate. */
const lookupCarById = carId => {
  const key = `car|${carId}`;
  if (!inFlight.has(key)) {
    const promise = searchCarModel({ carId }).finally(() => inFlight.delete(key));
    inFlight.set(key, promise);
  }
  return inFlight.get(key);
};

/**
 * A car chosen by model, put in Redux (reg null) and remembered — from its
 * payload, or fetched by its car-database id. Resolves to the payload, or null.
 */
export const selectModelCar = ({ carId = null, data = null }, { dispatch } = {}) => {
  const apply = payload => {
    if (!isCarPayload(payload)) return null;
    dispatch({ type: SET_LICENSE_PLATE_INFO, payload, reg: null });
    rememberCar({ reg: null, data: payload });
    return payload;
  };
  if (isCarPayload(data)) return Promise.resolve(apply(data));
  const id = carId != null ? String(carId) : '';
  if (!/^\d+$/.test(id)) return Promise.resolve(null);
  return lookupCarById(id).then(apply);
};

/**
 * Put a car in Redux (and storage), from a cached payload or a fresh lookup.
 * Resolves to the payload, or null when the plate gave no car. An entry
 * without a plate is a car chosen by model (selectModelCar).
 */
export const selectCar = (
  { reg, series = null, data = null, carId = null },
  { dispatch, userId } = {}
) => {
  const clean = normalizeReg(reg);
  if (!clean) return selectModelCar({ carId, data }, { dispatch });
  const apply = payload => {
    if (!isCarPayload(payload)) return null;
    dispatch({ type: SET_LICENSE_PLATE_INFO, payload, reg: clean });
    rememberCar({ reg: clean, series, data: payload });
    if (userId) {
      // Same write RegNumInput makes, so the next page load agrees on the car.
      updateUserProfileGeneral({
        params: { id: userId, publicData: { regNumber: clean, regSeries: series || null } },
      }).catch(() => {});
    }
    return payload;
  };
  if (isCarPayload(data)) return Promise.resolve(apply(data));
  return lookupPlate({ reg: clean, series }).then(apply);
};

/** Re-render when another component (or tab) remembers a car. */
export const useRecentCars = () => {
  const [recents, setRecents] = useState([]);
  useEffect(() => {
    const refresh = () => setRecents(getRecentCars());
    refresh();
    const onStorage = e => {
      if (e.key === RECENT_CARS_KEY) refresh();
    };
    window.addEventListener(CAR_EVENT, refresh);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CAR_EVENT, refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return recents;
};

/**
 * The current car, rehydrated into Redux on load when the page starts without
 * one. Mount it anywhere a fit result is shown; the lookup runs once however
 * many components use it.
 *
 * `reg` is the plate OF `plateInfo` — the one the car in Redux was looked up
 * with — whenever that is known. It used to be read on its own, from the
 * profile first, so a car restored from this browser could show up under the
 * profile's plate of another car (P25009-482: a Volvo XC60 as "WMM 191").
 *
 * @returns {{ plateInfo: Object|null, reg: string|null, loading: boolean, failed: boolean }}
 */
export const useCurrentCar = () => {
  const dispatch = useDispatch();
  const plateInfo = useSelector(state => state.currentLicensePlate?.currentLicensePlate || null);
  const plateInfoReg = useSelector(
    state => normalizeReg(state.currentLicensePlate?.currentLicensePlateReg) || null
  );
  const userPublicData = useSelector(
    state => state.user?.currentUser?.attributes?.profile?.publicData || null
  );
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const userReg = normalizeReg(userPublicData?.regNumber) || null;
  const userSeries = userPublicData?.regSeries || null;

  useEffect(() => {
    /* A car chosen by model (P25009-491): no plate to look up, and the
     * browser's choice wins over a signed-in profile's plate. */
    const stored = readStoredCar();
    if (stored && !normalizeReg(stored.reg)) {
      setReg(null);
      if (!plateInfo) dispatch({ type: SET_LICENSE_PLATE_INFO, payload: stored.data, reg: null });
      return undefined;
    }

    const storedReg = readStoredReg();
    // After forgetCar the profile still holds the old plate until the signed-in
    // user is fetched again; the cleared flag wins over it.
    const cleared = readString(CAR_CLEARED_KEY) === '1';
    /* This browser's car first: it is what the buyer last chose HERE, and
     * the profile can be older (a plate typed signed out, a profile write
     * that failed). The profile fills an empty browser — a new device, or
     * after logout. Browser first also means the same car loads whether the
     * signed-in user arrives before or after this effect runs. */
    const wantedReg = storedReg || (cleared ? null : userReg);
    setReg(wantedReg);
    if (plateInfo || !wantedReg) return undefined;

    if (stored && normalizeReg(stored.reg) === wantedReg) {
      dispatch({ type: SET_LICENSE_PLATE_INFO, payload: stored.data, reg: wantedReg });
      return undefined;
    }

    let cancelled = false;
    const series = storedReg ? readString(SERIES_STORAGE_KEY) : userSeries;
    setLoading(true);
    setFailed(false);
    lookupPlate({ reg: wantedReg, series })
      .then(data => {
        if (cancelled || !isCarPayload(data)) return;
        dispatch({ type: SET_LICENSE_PLATE_INFO, payload: data, reg: wantedReg });
        rememberCar({ reg: wantedReg, series, data });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [!!plateInfo, userReg, userSeries]);

  // The plate on screen follows the car in Redux when another component
  // swaps it ("Byt bil", the search page's input).
  useEffect(() => {
    const onChange = () => setReg(readStoredReg());
    window.addEventListener(CAR_EVENT, onChange);
    return () => window.removeEventListener(CAR_EVENT, onChange);
  }, []);

  return {
    plateInfo,
    reg: (plateInfo && plateInfoReg) || reg,
    loading: loading && !plateInfo,
    failed: failed && !plateInfo,
  };
};
